import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { maybeCaptureLocation } from '../utils/location';

const AuthContext = createContext();

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  error: null,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: parsedUser, token },
        });
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Establish a session directly (used after registration without an extra API call).
  // Memoized so effects depending on it don't re-run every render.
  const setSession = useCallback((user, token) => {
    if (!user || !token) return;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
  }, []);

  // A fresh human login is a new identity: drop any stale admin-visit backup
  // and tab impersonation keys so an old admin session can never leak into
  // (or be restored over) the new login. Never call from impersonation flow.
  const clearStaleImpersonation = () => {
    try {
      localStorage.removeItem('by_admin_backup');
      sessionStorage.removeItem('impersonate_token');
      sessionStorage.removeItem('impersonate_user');
    } catch {
      /* ignore */
    }
  };

  // Login function
  const login = async (credentials) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await authAPI.login(credentials);
      const { user, token } = response.data?.data || {};
      
      if (!token || !user) {
        return { success: false, error: 'Invalid response from server' };
      }
      
      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      clearStaleImpersonation();
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Admin login function (step 1: credentials -> email OTP)
  const adminLogin = async (credentials) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authAPI.adminLogin(credentials);
      const { user, token, otpRequired, email } = response.data?.data || {};

      // OTP step required — no tokens yet
      if (otpRequired) {
        dispatch({ type: 'LOGIN_FAILURE', payload: '' });
        return { success: true, otpRequired: true, email };
      }

      if (!token || !user) {
        return { success: false, error: 'Invalid response from server' };
      }

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Admin login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Admin OTP verification (step 2: completes login, stores tokens)
  const adminVerifyOtp = async ({ email, otp }) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authAPI.adminVerifyOtp({ email, otp });
      const { user, token } = response.data?.data || {};

      if (!token || !user) {
        return { success: false, error: 'Invalid response from server' };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      clearStaleImpersonation();

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'OTP verification failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await authAPI.register(userData);
      const { user, token } = response.data?.data || {};
      
      if (!token || !user) {
        return { success: false, error: 'Invalid response from server' };
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      clearStaleImpersonation();
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearStaleImpersonation();
    dispatch({ type: 'LOGOUT' });
  };

  // Login with mobile OTP
  const loginWithOtp = async (phone, otp) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authAPI.verifyOtp(phone, otp);
      const { user, token } = response.data?.data || {};
      if (!token || !user) return { success: false, error: 'Invalid response from server' };
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      clearStaleImpersonation();
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'OTP verification failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Social login (Google / Facebook)
  const loginWithSocial = async (provider, token, extra = {}) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authAPI.socialLogin(provider, token, extra);
      const { user, token: accessToken } = response.data?.data || {};
      if (!accessToken || !user) return { success: false, error: 'Invalid response from server' };
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      clearStaleImpersonation();
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: accessToken } });
      await maybeCaptureLocation(user);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Social login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Handle Google redirect login return (id_token in URL fragment)
  useEffect(() => {
    const hash = window.location.hash || '';
    if (hash.indexOf('id_token=') === -1) return;
    const params = new URLSearchParams(hash.slice(1));
    const idToken = params.get('id_token');
    const nonce = sessionStorage.getItem('google_nonce') || '';
    const partnerType = sessionStorage.getItem('google_partner_type') || '';
    const back = sessionStorage.getItem('google_redirect') || '/';
    sessionStorage.removeItem('google_nonce');
    sessionStorage.removeItem('google_partner_type');
    sessionStorage.removeItem('google_redirect');
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    if (!idToken || !nonce) return;
    loginWithSocial('google', idToken, { nonce, partnerType: partnerType || undefined }).then((r) => {
      if (partnerType) {
        const dest = partnerType === 'hotel' ? '/partner/hotel/dashboard' : partnerType === 'restaurant' ? '/partner/restaurant/dashboard' : partnerType === 'bus' ? '/partner/bus/dashboard' : '/partner/login';
        window.location.assign(r.success ? dest : '/partner/login');
        return;
      }
      const dest = (back === '/login' || back === '/register' || back.indexOf('/login') === 0 || back.indexOf('/register') === 0) ? '/my-profile' : back;
      window.location.assign(r.success ? dest : '/login');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update user function
  const updateUser = (userData) => {
    const updatedUser = { ...state.user, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Check if user is admin
  const isAdmin = () => {
    return state.user?.role === 'admin';
  };


  const value = {
    ...state,
    login,
    adminLogin,
    adminVerifyOtp,
    register,
    setSession,
    logout,
    loginWithOtp,
    loginWithSocial,
    updateUser,
    clearError,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

